import { useEffect, useState } from "react";
import { getAuditLogs, cleanupOldAttendancePhotos } from "../../firebase/services";
import { format } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";

// TYPE_LABELS will be built inside the component using t() so they are reactive to language changes

const CLEANUP_OPTIONS = [
  { label: "Cũ hơn 3 tháng", value: 3 },
  { label: "Cũ hơn 6 tháng", value: 6 },
  { label: "Cũ hơn 12 tháng", value: 12 },
];

export default function AdminAuditLog() {
  const { t } = useLanguage();
  const TYPE_LABELS = {
    // Đăng nhập / tài khoản
    login:                { label: t("audit.login"),            badge: "badge-gray" },
    logout:               { label: t("audit.logout"),            badge: "badge-gray" },
    password_changed:     { label: t("audit.password_changed"),  badge: "badge-gray" },
    // Promoter / supervisor
    promoter_created:     { label: t("audit.promoter_created"),  badge: "badge-green" },
    promoter_updated:     { label: t("audit.promoter_updated"),  badge: "badge-amber" },
    promoter_archived:    { label: t("audit.promoter_archived"), badge: "badge-red" },
    promoter_restored:    { label: t("audit.promoter_restored"), badge: "badge-green" },
    supervisor_created:   { label: t("audit.supervisor_created"),badge: "badge-green" },
    assignment_changed:   { label: t("audit.assignment_changed"),badge: "badge-blue" },
    // Doanh số
    sale_submitted:       { label: t("audit.sale_submitted"),    badge: "badge-blue" },
    sale_approved:        { label: t("audit.sale_approved"),        badge: "badge-green" },
    sale_rejected:        { label: t("audit.sale_rejected"),        badge: "badge-red" },
    sale_cancelled:       { label: t("audit.sale_cancelled"),       badge: "badge-red" },
    sale_cancel_rejected: { label: t("audit.sale_cancel_rejected"), badge: "badge-gray" },
    sale_highend_changed: { label: t("audit.sale_highend_changed"), badge: "badge-amber" },
    sale_approval_reverted: { label: t("audit.sale_approval_reverted"), badge: "badge-amber" },
    // Chấm công / giải trình
    attendance_checkin:   { label: t("audit.attendance_checkin"),   badge: "badge-green" },
    attendance_day_cleared: { label: t("audit.attendance_day_cleared"), badge: "badge-gray" },
    leave_requested:      { label: t("audit.leave_requested"),      badge: "badge-amber" },
    leave_approved:       { label: t("audit.leave_approved"),       badge: "badge-green" },
    leave_rejected:       { label: t("audit.leave_rejected"),       badge: "badge-red" },
    tax_config_saved:     { label: t("audit.tax_config_saved"),     badge: "badge-purple" },
    insurance_adjusted:   { label: t("audit.insurance_adjusted"),   badge: "badge-amber" },
    attendance_checkout:  { label: t("audit.attendance_checkout"),  badge: "badge-green" },
    appeal_submitted:     { label: t("audit.appeal_submitted"),     badge: "badge-amber" },
    appeal_approved:      { label: t("audit.appeal_approved"),      badge: "badge-green" },
    appeal_rejected:      { label: t("audit.appeal_rejected"),      badge: "badge-red" },
    // Cấu hình / danh mục
    target_set:           { label: t("audit.target_set"),           badge: "badge-blue" },
    target_approved:      { label: t("audit.target_approved"),   badge: "badge-green" },
    bonus_program_added:  { label: t("audit.bonus_program_added"),  badge: "badge-purple" },
    bonus_config_saved:   { label: t("audit.bonus_config_saved"),badge: "badge-purple" },
    event_bonus_added:    { label: t("audit.event_bonus_added"), badge: "badge-purple" },
    event_bonus_deleted:  { label: t("audit.event_bonus_deleted"),badge: "badge-red" },
    sku_added:            { label: t("audit.sku_added"),         badge: "badge-green" },
    sku_deleted:          { label: t("audit.sku_deleted"),       badge: "badge-red" },
    holiday_added:        { label: t("audit.holiday_added"),     badge: "badge-green" },
    holiday_deleted:      { label: t("audit.holiday_deleted"),   badge: "badge-red" },
    banner_saved:         { label: t("audit.banner_saved"),      badge: "badge-blue" },
    banner_deleted:       { label: t("audit.banner_deleted"),    badge: "badge-red" },
    training_added:       { label: t("audit.training_added"),    badge: "badge-green" },
    training_updated:     { label: t("audit.training_updated"),  badge: "badge-amber" },
    training_deleted:     { label: t("audit.training_deleted"),  badge: "badge-red" },
    // Thông báo / import
    notification_sent:    { label: t("audit.notification_sent"),    badge: "badge-blue" },
    income_imported:      { label: t("audit.income_imported"),      badge: "badge-blue" },
    payroll_adjusted:     { label: t("audit.payroll_adjusted"),  badge: "badge-amber" },
    payroll_closed:       { label: t("audit.payroll_closed"),    badge: "badge-green" },
    payroll_reopened:     { label: t("audit.payroll_reopened"),  badge: "badge-amber" },
    payroll_config_saved: { label: t("audit.payroll_config_saved"), badge: "badge-purple" },
    payroll_config_deleted: { label: t("audit.payroll_config_deleted"), badge: "badge-red" },
    ot_requested:         { label: t("audit.ot_requested"),         badge: "badge-amber" },
    ot_approved:          { label: t("audit.ot_approved"),          badge: "badge-green" },
    ot_rejected:          { label: t("audit.ot_rejected"),          badge: "badge-red" },
    ot_withdrawn:         { label: t("audit.ot_withdrawn"),         badge: "badge-gray" },
  };
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterMonth, setFilterMonth] = useState("");
  const [search, setSearch] = useState("");

  // Cleanup state
  const [cleanupMonths, setCleanupMonths] = useState(3);
  const [cleaning, setCleaning] = useState(false);
  const [cleanProgress, setCleanProgress] = useState(null); // { done, total }
  const [cleanResult, setCleanResult] = useState(null);

  const load = async () => {
    setLoading(true);
    const all = await getAuditLogs();
    setLogs(all);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const q = search.trim().toLowerCase();
  const filtered = logs.filter(l => {
    if (filterType !== "all" && l.type !== filterType) return false;
    if (filterMonth) {
      const logMonth = l.meta?.month || l.createdAt?.toDate?.()?.toISOString?.().slice(0, 7) || "";
      if (!logMonth.startsWith(filterMonth)) return false;
    }
    if (q) {
      const hay = `${l.actorName || ""} ${l.promoterName || ""} ${l.detail || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const handleCleanup = async () => {
    if (!window.confirm(`Xoá ảnh attendance cũ hơn ${cleanupMonths} tháng?\n\nHành động này không thể hoàn tác. File sẽ bị xoá khỏi Firebase Storage.`)) return;
    setCleaning(true);
    setCleanProgress({ done: 0, total: "?" });
    setCleanResult(null);
    try {
      const count = await cleanupOldAttendancePhotos(cleanupMonths, (done, total) => {
        setCleanProgress({ done, total });
      });
      setCleanResult({ success: true, count });
    } catch (err) {
      setCleanResult({ success: false, error: err.message });
    } finally {
      setCleaning(false);
      setCleanProgress(null);
    }
  };

  return (
    <>
      <div className="page-header">
        <div><h1>{t("audit.title")}</h1><p>{t("audit.subtitle")}</p></div>
        <button className="btn" onClick={load} disabled={loading}>🔄 {t("common.refresh")}</button>
      </div>

      {/* ── Photo Cleanup Card ── */}
      <div className="card" style={{ marginBottom: 20, borderLeft: "4px solid #f59e0b" }}>
        <div className="card-header" style={{ marginBottom: 8 }}>
          <div className="card-title">{t("audit.cleanup_title")}</div>
        </div>
        <p style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
          {t("audit.cleanup_desc")}
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <select value={cleanupMonths} onChange={e => setCleanupMonths(Number(e.target.value))} style={{ width: 200 }} disabled={cleaning}>
            {CLEANUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button className="btn" style={{ background: "#f59e0b", color: "#fff", borderColor: "#f59e0b" }} onClick={handleCleanup} disabled={cleaning}>
            {cleaning ? `Đang xoá... (${cleanProgress?.done}/${cleanProgress?.total})` : "🗑 Bắt đầu dọn dẹp"}
          </button>
        </div>
        {cleanResult && (
          <div className={`alert ${cleanResult.success ? "alert-success" : "alert-error"}`} style={{ marginTop: 10 }}>
            {cleanResult.success
              ? `✅ Đã xoá ảnh của ${cleanResult.count} bản ghi chấm công.`
              : `❌ Lỗi: ${cleanResult.error}`}
          </div>
        )}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ minWidth: 200 }}>
          <option value="all">{t("audit.filter_all")} ({logs.length})</option>
          {Object.entries(TYPE_LABELS).map(([key, info]) => (
            <option key={key} value={key}>{info.label}</option>
          ))}
        </select>
        <input
          placeholder={t("sales.search_ph")}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 220, fontSize: 13 }}
        />
        <input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ width: 150 }} />
        {filterMonth && <button className="btn btn-sm" onClick={() => setFilterMonth("")}>✕</button>}
        <span style={{ fontSize: 12, color: "#888" }}>{filtered.length} {t("x.records")}</span>
      </div>

      {/* ── Log table ── */}
      <div className="card">
        {loading ? (
          <div style={{ color: "#aaa", textAlign: "center", padding: 32 }}>Đang tải...</div>
        ) : (
          <table className="table-card">
            <thead>
              <tr>
                <th>{t("audit.time")}</th>
                <th>{t("audit.type")}</th>
                <th>{t("audit.actor")}</th>
                <th>{t("audit.promoter")}</th>
                <th>{t("audit.detail")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(l => {
                const typeInfo = TYPE_LABELS[l.type] || { label: l.type, badge: "badge-gray" };
                const ts = l.createdAt?.toDate ? format(l.createdAt.toDate(), "dd/MM/yyyy HH:mm") : "—";
                return (
                  <tr key={l.id}>
                    <td data-label="Thời gian" style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>{ts}</td>
                    <td data-label="Loại"><span className={`badge ${typeInfo.badge}`}>{typeInfo.label}</span></td>
                    <td data-label="Người thực hiện">
                      <strong>{l.actorName || "—"}</strong>
                      <br /><span style={{ fontSize: 11, color: "#aaa" }}>{l.actorRole === "supervisor" ? t("audit.supervisor_label") : l.actorRole === "promoter" ? "Promoter" : l.actorRole === "admin" ? t("audit.admin_label") : (l.actorRole || "")}</span>
                    </td>
                    <td data-label="Promoter">{l.promoterName || "—"}</td>
                    <td data-label="Chi tiết" style={{ fontSize: 12, color: "#555" }}>{l.detail}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ color: "#aaa", textAlign: "center", padding: 32 }}>{t("common.noData")}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
